import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';
import { getContext } from '../../redux/spaces-slice';
import fallbackBrandLogo from '../../../../assets/images/cisco_brand_logo.svg';
import { resolveBrandingUrl } from './resolve-branding-url';
const BrandingContainer = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  /* Hug the logo (fit-content) so the pill wraps it snugly, but keep a small
     min-width so an intrinsic-less logo can't collapse the pill to nothing.
     The pill sizes to the logo plus padding, so padding stays even on all
     sides regardless of logo dimensions. */
  width: fit-content;
  min-width: 3vw;
  border-radius: 0.833vw;
  background-color: #1f2226;
  padding: 1.6vh 1.5vw;
  overflow: hidden;
`;
const Logo = styled.img `
  display: block;
  object-fit: contain;

  ${({ $portrait }) => $portrait
    ? css `
          /* Portrait/tall logos: a short, wide header can't show a tall logo at
             any real size when sized by the header height, so let it grow
             vertically into a badge instead. Width follows the aspect ratio and
             is capped so it never becomes a wide block. */
          height: clamp(4.4rem, 9vh, 6.2rem);
          width: auto;
          max-width: clamp(4.4rem, 6vw, 7rem);
        `
    : css `
          /* Landscape/square logos: unchanged height-driven sizing. */
          height: 4.08vh;
          width: auto;
          max-width: clamp(7.2rem, 10.8vw, 13.2rem);
        `}
`;
export default function BrandLogo() {
    const context = useSelector(getContext);
    // Portrait (tall) logos get squeezed to a thin sliver when sized by the short
    // header height, so we measure the loaded image and switch to badge-style
    // (vertical) sizing only for those. Landscape/square logos stay untouched.
    const [isPortrait, setIsPortrait] = React.useState(false);
    const handleLogoLoad = (event) => {
        const img = event.currentTarget;
        if (img.naturalWidth && img.naturalHeight) {
            setIsPortrait(img.naturalHeight > img.naturalWidth * 1.1);
        }
    };
    // Ordered list of logo sources to try, most-preferred first:
    //   1. CloudFront proxy (raw upstream URL rewritten to the kiosk origin)
    //   2. Raw upstream URL straight from contextInfo (e.g. S3)
    //   3. Default Cisco logo
    // Falsy/duplicate entries are removed so each candidate is tried once.
    const logoSources = React.useMemo(() => {
        const proxied = resolveBrandingUrl(context.brandingUrl);
        const ordered = [proxied, context.brandingUrl, fallbackBrandLogo];
        return ordered.filter((src, index) => Boolean(src) && ordered.indexOf(src) === index);
    }, [context.brandingUrl]);
    const handleLogoError = (event) => {
        var _a;
        const img = event.currentTarget;
        const currentIndex = Number((_a = img.dataset.sourceIndex) !== null && _a !== void 0 ? _a : '0');
        const nextIndex = currentIndex + 1;
        // Advance to the next candidate; stop once every source has been tried
        // (prevents an infinite error loop if even the default logo fails).
        if (nextIndex >= logoSources.length) {
            return;
        }
        img.dataset.sourceIndex = String(nextIndex);
        img.src = logoSources[nextIndex];
    };
    return (_jsx(BrandingContainer, { children: _jsx(Logo, { src: logoSources[0], "data-source-index": "0", alt: "Brand Logo", "$portrait": isPortrait, onLoad: handleLogoLoad, onError: handleLogoError }) }));
}
